import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { useTranslation } from 'react-i18next';
import { renderToStaticMarkup } from 'react-dom/server';
import Hidden from 'raydiant-elements/layout/Hidden';
import { Popover } from '@headlessui/react';
import { ContentDetailByDay } from '@raydiant/api-client-js';
import moment from 'moment';

import {
  CHART_DEFAULT_AXIS_OPTIONS,
  CHART_DEFAULT_OPTIONS,
  formatDateLabel,
} from '../utils';
import { colorsForContentDetailCharts, iconForLegend } from './utils';
import ChartToolTip, { EChartsToolTipParams } from '../ChartToolTip';
import { getDisplayValue } from '../ShopperEngagementInsights/utils';
import ChartCard from '../ChartCard/ChartCard';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import config from '../../../config';

const { supportAppUrl } = config;

echarts.use([LineChart]);

const color = [...colorsForContentDetailCharts];

type VerifiedViewsOverTimeChartView = keyof ContentDetailByDay;

type VerifiedViewsOverTimeChartProps = {
  contentData: ContentDetailByDay[];
  presentationName: string;
};

const getXAxisData = (data: ContentDetailByDay[]) => {
  const firstItemDate = moment(data[0].timestamp).format('MMM YYYY');
  const lastItemDate = moment(data[data.length - 1].timestamp).format(
    'MMM YYYY',
  );
  if (firstItemDate !== lastItemDate) {
    return data.map((value) =>
      formatDateLabel(value.timestamp, 'ddd D[\n]MMMM'),
    );
  }
  return data.map((value, index) => {
    return index === 0
      ? formatDateLabel(value.timestamp, 'ddd D[\n]MMMM')
      : formatDateLabel(value.timestamp, 'ddd D');
  });
};

const getYAxisData = (
  data: ContentDetailByDay[],
  view: VerifiedViewsOverTimeChartView,
) => data?.map((value) => value[view]);

export default function VerifiedViewsOverTimeChart({
  contentData,
  presentationName,
}: VerifiedViewsOverTimeChartProps) {
  const { t } = useTranslation();

  const isEmptyData = !contentData.length;
  const playbackTimeDataMins = contentData.map((item) =>
    Math.round((Number(item.playbackDuration) || 0) / 60),
  );

  const getSeries = (data: ContentDetailByDay[]) => {
    let series = [];

    series.push({
      areaStyle: {
        opacity: 0.7,
        color: colorsForContentDetailCharts[0],
      },
      data: getYAxisData(data, 'verifiedViews'),
      emphasis: {
        focus: 'series',
      },
      lineStyle: {
        width: 0,
      },
      name: t('analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle1'),
      showSymbol: false,
      smooth: false,
      stack: 'Verified Views',
      type: 'line',
      z: 2,
    });

    series.push({
      areaStyle: {
        opacity: 1,
        color: colorsForContentDetailCharts[1],
      },
      data: playbackTimeDataMins,
      emphasis: {
        focus: 'series',
      },
      lineStyle: {
        width: 0,
      },
      name: t('analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle2'),
      smooth: false,
      showSymbol: false,
      stack: 'Playback Duration',
      type: 'line',
      yAxisIndex: 1,
      z: 1,
    });

    return series;
  };

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    color: color,
    grid: {
      ...CHART_DEFAULT_OPTIONS.grid,
      top: 50,
    },
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: t(
            'analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle1',
          ),
          icon: iconForLegend,
        },
        {
          name: t(
            'analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle2',
          ),
          icon: iconForLegend,
        },
      ],
    },
    xAxis: {
      ...CHART_DEFAULT_AXIS_OPTIONS.xAxis,
      data: getXAxisData(contentData),
      name: presentationName.toUpperCase(),
      nameTextStyle: {
        width: '80%',
        fontWeight: 900,
        fontSize: 16,
        backgroundColor: '#F5F5F5',
        height: 40,
        textAlign: 'center',
        color: '#000',
        lineHeight: 40,
        padding: [0, 120],
      },
      nameGap: 50,
      axisLabel: {
        ...CHART_DEFAULT_AXIS_OPTIONS.xAxis.axisLabel,
        fontSize: 12,
        lineHeight: 20,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: [
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
        name: t(
          'analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle1',
        ).toUpperCase(),
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.yAxis.axisLabel,
          nameGap: 40,
        },
      },
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
        alignTicks: true,
        name: `${t(
          'analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle2',
        )} (mins)`.toUpperCase(),
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.yAxis.axisLabel,
          margin: 8,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'solid',
            color: '#FDF0F0',
            width: 1,
          },
        },
      },
    ],
    series: getSeries(contentData),
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      formatter: (params: EChartsToolTipParams[]) => {
        const index = params[0]?.dataIndex;
        const value = contentData[index];

        const modifiedParams: EChartsToolTipParams[] = params.map((item) => {
          if (
            item.seriesName ===
            t('analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle2')
          ) {
            return {
              ...item,
              value: getDisplayValue(
                Math.round(Number(item.value) || 0),
                ' min',
              ),
            };
          } else {
            return { ...item, value: getDisplayValue(item.value) };
          }
        });

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 text-black">
                {formatDateLabel(value.timestamp, 'ddd, MMMM D, YYYY')}
              </p>
            }
            values={modifiedParams}
          />,
        );
      },
    },
  };

  const mobileOptions = {
    ...options,
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: t(
            'analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle1',
          ),
          icon: iconForLegend,
        },
        {
          name: t(
            'analytics.contentDetailPage.verifiedViewsOverTime.yAxisTitle2',
          ),
          icon: iconForLegend,
        },
      ],
      type: 'scroll',
    },
    series: getSeries(contentData),
  };

  return (
    <ChartCard
      subTitle={''}
      title={
        <div className="flex gap-3 items-center">
          <h2 className="text-xl font-bold text-black">
            {t('analytics.contentDetailPage.verifiedViewsOverTime.title')}
          </h2>
          <Popover className="relative">
            <Popover.Button>
              <QuestionMarkIcon />
            </Popover.Button>
            <Popover.Panel className="absolute z-10 card popover">
              <div className="overflow-hidden">
                <p className="mb-3">
                  {t(
                    'analytics.contentDetailPage.verifiedViewsOverTime.tooltip',
                  )}
                </p>
                <a
                  href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('analytics.learnmore')}
                </a>
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      }
      showTimeInterval={false}
    >
      <div className="min-w-full min-h-full">
        {isEmptyData ? (
          <div className="w-full text-center">
            {t('analytics.contentDetailPage.emptyData.emptyChartData')}
          </div>
        ) : (
          <>
            <Hidden xsDown>
              <ReactECharts
                style={{ height: '460px' }}
                option={options}
                onChartReady={(chart) => chart.resize()}
              />
            </Hidden>
            <Hidden smUp>
              <ReactECharts
                style={{ height: '460px' }}
                option={mobileOptions}
                onChartReady={(chart) => chart.resize()}
              />
            </Hidden>
          </>
        )}
      </div>
    </ChartCard>
  );
}
